import React from "react";
import PropTypes from "prop-types";
import { formatPrice } from "../../../utils/priceUtils";
import "./CompareLegend.css";

/**
 * CompareLegend - Displays legend for comparison symbols
 * Shows each symbol's color, name, and current close price (real values, not percentages)
 *
 * Uses HTML absolute positioning for reliable rendering.
 * Position can be controlled via the top prop to stack with other tooltips.
 */
const CompareLegend = React.memo(
  ({
    mainSymbol,
    comparisonSymbols,
    currentItem,
    plotData,
    onRemoveSymbol,
    onClearAll,
    themeColors,
  }) => {
    if (!comparisonSymbols || comparisonSymbols.length === 0) {
      return null;
    }

    // Get current data point for displaying prices
    const dataPoint =
      currentItem || (plotData && plotData[plotData.length - 1]);

    // Format price value - show real close price
    const formatClosePrice = (symbol, value) => {
      if (value === null || value === undefined) return "n/a";
      try {
        return formatPrice(symbol, value);
      } catch {
        return value.toFixed(2);
      }
    };

    const isDarkMode = themeColors?.isDark === true;

    // Get main symbol's close price
    const mainClose = dataPoint?.close;

    return (
      <div className={`compare-legend ${isDarkMode ? "dark" : "light"}`}>
        {/* Main symbol row */}
        <div className="compare-legend-row">
          <span
            className="compare-legend-color"
            style={{ backgroundColor: themeColors?.text || "#ffffff" }}
          />
          <span className="compare-legend-symbol">{mainSymbol}</span>
          <span className="compare-legend-price">
            {formatClosePrice(mainSymbol, mainClose)}
          </span>
        </div>

        {/* Comparison symbol rows */}
        {comparisonSymbols.map(({ symbol, color }) => {
          const closeKey = `compare_${symbol}_close`;
          const closePrice = dataPoint?.[closeKey];

          return (
            <div key={symbol} className="compare-legend-row">
              <span
                className="compare-legend-color"
                style={{ backgroundColor: color }}
              />
              <span className="compare-legend-symbol">{symbol}</span>
              <span className="compare-legend-price">
                {formatClosePrice(symbol, closePrice)}
              </span>
              {onRemoveSymbol && (
                <button
                  className="compare-legend-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSymbol(symbol);
                  }}
                  title="Remover"
                  data-evo-id={`compare-legend-remove-${symbol}`}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}

        {/* Clear all button */}
        {onClearAll && comparisonSymbols.length > 0 && (
          <div className="compare-legend-footer">
            <button
              className="compare-legend-clear"
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              data-evo-id="compare-legend-clear"
            >
              Limpar
            </button>
          </div>
        )}
      </div>
    );
  },
);

CompareLegend.displayName = "CompareLegend";

CompareLegend.propTypes = {
  mainSymbol: PropTypes.string.isRequired,
  comparisonSymbols: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ),
  currentItem: PropTypes.object,
  plotData: PropTypes.array,
  onRemoveSymbol: PropTypes.func,
  onClearAll: PropTypes.func,
  themeColors: PropTypes.object,
};

export default CompareLegend;
